import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { collection, query, where, getDocs, doc, updateDoc, increment } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { 
  Phone, 
  Mail, 
  MessageCircle, 
  MapPin, 
  Instagram, 
  Facebook, 
  Linkedin, 
  Youtube,
  ExternalLink,
  Download,
  Share2,
  Globe,
  Twitter,
  ChevronDown
} from 'lucide-react'
import { cardTemplates } from '../templates/cardTemplates'
import { SimpleIdentityTemplate } from '../templates/SimpleIdentity'
import { ModernGradientTemplate } from '../templates/ModernGradient'
import { DarkNeonTemplate } from '../templates/DarkNeon'
import { GoldMetalTemplate } from '../templates/GoldMetal'
import { BlackMarbleTemplate } from '../templates/BlackMarble'
import { TealCardTemplate } from '../templates/TealCard'
import '../templates/SimpleIdentity.css'
import '../templates/ModernGradient.css'
import '../templates/DarkNeon.css'
import '../templates/GoldMetal.css'
import '../templates/BlackMarble.css'
import '../templates/TealCard.css'
import './VCard.css'
import SEO from '../components/SEO'

export default function VCardPage() {
  const { username } = useParams()
  const [vcard, setVcard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showFullAbout, setShowFullAbout] = useState(false)

  useEffect(() => {
    async function fetchVCard() {
      try {
        const q = query(collection(db, 'vcards'), where('username', '==', username))
        const querySnapshot = await getDocs(q)
        
        if (querySnapshot.empty) {
          setError('vCard not found')
          setLoading(false)
          return
        }

        const vcardData = querySnapshot.docs[0].data()
        setVcard(vcardData)

        // --- Real Analytics Tracking ---
        try {
          const analyticsRef = doc(db, 'analytics', vcardData.uid)
          const todayRequest = new Date()
          const dateKey = `daily_${todayRequest.getFullYear()}_${String(todayRequest.getMonth() + 1).padStart(2, '0')}_${String(todayRequest.getDate()).padStart(2, '0')}`
          
          // Determine Traffic Source
          const params = new URLSearchParams(window.location.search)
          const sourceParam = params.get('source')
          let source = 'direct'
          if (sourceParam === 'qr') source = 'qr'
          else if (document.referrer && document.referrer.includes('instagram')) source = 'social'
          else if (document.referrer && document.referrer.includes('facebook')) source = 'social'
          else if (document.referrer && document.referrer.includes('linkedin')) source = 'social'
          else if (document.referrer && document.referrer.includes('t.co')) source = 'social'

          // Activity Item
          const activityItem = {
            type: 'Top_View',
            detail: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            date: new Date().toISOString()
          }

          // Use setDoc with merge: true to handle both new and existing docs safely
          await setDoc(analyticsRef, {
            visits: increment(1),
            qrScans: source === 'qr' ? increment(1) : increment(0),
            lastVisited: new Date(),
            [dateKey]: increment(1), // Dynamic daily key
            [`sources.${source}`]: increment(1), // Dynamic source key
             // Note: arrayUnion adds unique elements only. 
             // To log "visits", we might want a localized "latest" activity or just specialized events.
             // For now, let's just log unique "actions" or keep it simple.
             // We'll skip arrayUnion for simple views to avoid exploding doc size, 
             // but maybe log just the "Last Activity" string.
             lastActivityType: 'Viewed vCard',
             lastActivityTime: new Date()
          }, { merge: true })

        } catch (e) {
          console.log('Analytics update skipped', e)
        }
      } catch (error) {
        console.error('Error fetching vCard:', error)
        setError('Failed to load vCard')
      } finally {
        setLoading(false)
      }
    }

    fetchVCard()
  }, [username])

  function generateVCF() {
    if (!vcard) return

    const vcfContent = `BEGIN:VCARD
VERSION:3.0
FN:${vcard.name || ''}
TITLE:${vcard.title || ''}
TEL:${vcard.phone || ''}
EMAIL:${vcard.email || ''}
ADR:;;${vcard.address || ''}
NOTE:${vcard.about || ''}
END:VCARD`

    const blob = new Blob([vcfContent], { type: 'text/vcard' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${vcard.name || 'contact'}.vcf`
    link.click()
    URL.revokeObjectURL(url)
  }

  function shareVCard() {
    const url = window.location.href
    if (navigator.share) {
      navigator.share({
        title: `${vcard?.name}'s Digital vCard`,
        text: 'Check out my digital business card!',
        url
      })
    } else {
      navigator.clipboard.writeText(url)
      alert('Link copied to clipboard!')
    }
  }

  // Schema.org Structured Data
  const personSchema = vcard ? {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: vcard.name,
    jobTitle: vcard.title,
    description: vcard.about,
    image: vcard.profileImage,
    telephone: vcard.phone,
    email: vcard.email,
    url: window.location.href,
    sameAs: [
      vcard.social?.linkedin ? `https://linkedin.com/in/${vcard.social.linkedin}` : null,
      vcard.social?.twitter ? `https://twitter.com/${vcard.social.twitter.replace('@', '')}` : null,
      vcard.social?.facebook ? `https://facebook.com/${vcard.social.facebook}` : null,
      vcard.social?.instagram ? `https://instagram.com/${vcard.social.instagram.replace('@', '')}` : null,
      vcard.social?.website ? (vcard.social.website.startsWith('http') ? vcard.social.website : `https://${vcard.social.website}`) : null
    ].filter(Boolean)
  } : null

  if (loading) {
    return (
      <div className="vcard-loading">
        <div className="loading-pulse"></div>
        <p>Loading vCard...</p>
      </div>
    )
  }

  // Check for plan expiration
  const isExpired = vcard?.plan !== 'free' && vcard?.expiresAt && vcard.expiresAt.toDate() < new Date()

  if (vcard && isExpired) {
    return (
      <div className="vcard-error">
        <div className="error-icon">🔒</div>
        <h2>Profile Unavailable</h2>
        <p>This digital vCard is currently inactive.</p>
        <a href="/" className="btn btn-primary">Create Your Own vCard</a>
      </div>
    )
  }

  if (error) {
    return (
      <div className="vcard-error">
        <div className="error-icon">😕</div>
        <h2>{error}</h2>
        <p>The vCard you're looking for doesn't exist or has been removed.</p>
        <a href="/" className="btn btn-primary">Go to Homepage</a>
      </div>
    )
  }

  // Get template or use default
  const templateId = vcard?.template || 'simple-identity'
  const template = cardTemplates.find(t => t.id === templateId) || cardTemplates[0]

  // Render Simple Identity template
  if (templateId === 'simple-identity') {
    return (
      <>
        <SEO 
          title={`${vcard.name} | ${vcard.title || 'Digital vCard'}`}
          description={vcard.about?.substring(0, 150) || `Contact details for ${vcard.name}`}
          image={vcard.profileImage}
          schema={personSchema}
        />
        <SimpleIdentityTemplate 
          vcard={vcard} 
          onSaveContact={generateVCF} 
          onShare={shareVCard} 
        />
      </>
    )
  }

  // Render Modern Gradient template
  if (templateId === 'modern-gradient') {
    return (
      <>
        <SEO 
          title={`${vcard.name} | ${vcard.title || 'Digital vCard'}`}
          description={vcard.about?.substring(0, 150) || `Contact details for ${vcard.name}`}
          image={vcard.profileImage}
          schema={personSchema}
        />
        <ModernGradientTemplate 
          vcard={vcard} 
          onSaveContact={generateVCF} 
          onShare={shareVCard} 
        />
      </>
    )
  }

  // Render Dark Neon template
  if (templateId === 'dark-neon') {
    return (
      <>
        <SEO 
          title={`${vcard.name} | ${vcard.title || 'Digital vCard'}`}
          description={vcard.about?.substring(0, 150) || `Contact details for ${vcard.name}`}
          image={vcard.profileImage}
          schema={personSchema}
        />
        <DarkNeonTemplate 
          vcard={vcard} 
          onSaveContact={generateVCF} 
          onShare={shareVCard} 
        />
      </>
    )
  }

  // Render Gold Metal template
  if (templateId === 'gold-metal') {
    return (
      <>
        <SEO 
          title={`${vcard.name} | ${vcard.title || 'Digital vCard'}`}
          description={vcard.about?.substring(0, 150) || `Contact details for ${vcard.name}`}
          image={vcard.profileImage}
          schema={personSchema}
        />
        <GoldMetalTemplate 
          vcard={vcard} 
          onSaveContact={generateVCF} 
          onShare={shareVCard} 
        />
      </>
    )
  }

  // Render Black Marble template
  if (templateId === 'black-marble') {
    return (
      <>
        <SEO 
          title={`${vcard.name} | ${vcard.title || 'Digital vCard'}`}
          description={vcard.about?.substring(0, 150) || `Contact details for ${vcard.name}`}
          image={vcard.profileImage}
          schema={personSchema}
        />
        <BlackMarbleTemplate 
          vcard={vcard} 
          onSaveContact={generateVCF} 
          onShare={shareVCard} 
        />
      </>
    )
  }

  // Render Teal Card template
  if (templateId === 'teal-card') {
    return (
      <>
        <SEO 
          title={`${vcard.name} | ${vcard.title || 'Digital vCard'}`}
          description={vcard.about?.substring(0, 150) || `Contact details for ${vcard.name}`}
          image={vcard.profileImage}
          schema={personSchema}
        />
        <TealCardTemplate 
          vcard={vcard} 
          onSaveContact={generateVCF} 
          onShare={shareVCard} 
        />
      </>
    )
  }

  // Default template rendering
  return (
    <div className={`vcard-page template-${templateId}`}>
      <SEO 
        title={`${vcard.name} | ${vcard.title || 'Digital vCard'}`}
        description={vcard.about?.substring(0, 150) || `Contact details for ${vcard.name}`}
        image={vcard.profileImage}
        schema={personSchema}
      />
      {/* Header Section */}
      <header 
        className="vcard-header"
        style={{ background: template.style.headerBg }}
      >
        {template.style.hasWave && (
          <div className="header-wave">
            <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path fill={template.style.cardBg} d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
            </svg>
          </div>
        )}
      </header>

      {/* Main Card */}
      <div className="vcard-container" style={{ background: template.style.cardBg }}>
        {/* Profile Section */}
        <div className="vcard-profile">
          <div 
            className={`vcard-avatar ${template.style.avatarStyle}`}
            style={{ 
              backgroundImage: vcard.profileImage ? `url(${vcard.profileImage})` : 'none',
              borderColor: template.style.accentColor
            }}
          >
            {!vcard.profileImage && (
              <span style={{ color: template.style.accentColor }}>
                {vcard.name?.[0] || 'U'}
              </span>
            )}
          </div>
          <h1 className="vcard-name" style={{ color: template.style.textColor }}>
            {vcard.name || 'User'}
          </h1>
          {vcard.title && (
            <p className="vcard-title" style={{ color: template.style.accentColor }}>
              {vcard.title}
            </p>
          )}
        </div>

        {/* Quick Contact Buttons */}
        <div className="vcard-actions">
          {vcard.phone && (
            <a 
              href={`tel:${vcard.phone}`} 
              className={`action-btn ${template.style.buttonStyle}`}
              style={{ 
                '--btn-color': template.style.accentColor,
                '--btn-bg': template.style.accentColor
              }}
            >
              <Phone size={20} />
              <span>Call</span>
            </a>
          )}
          {vcard.whatsapp && (
            <a 
              href={`https://wa.me/${vcard.whatsapp.replace(/\D/g, '')}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className={`action-btn whatsapp ${template.style.buttonStyle}`}
            >
              <MessageCircle size={20} />
              <span>WhatsApp</span>
            </a>
          )}
          {vcard.email && (
            <a 
              href={`mailto:${vcard.email}`} 
              className={`action-btn ${template.style.buttonStyle}`}
              style={{ 
                '--btn-color': template.style.accentColor,
                '--btn-bg': template.style.accentColor
              }}
            >
              <Mail size={20} />
              <span>Email</span>
            </a>
          )}
        </div>

        {/* About Section */}
        {vcard.about && (
          <section className="vcard-section">
            <h3 className="section-label" style={{ color: template.style.accentColor }}>
              About
            </h3>
            <p 
              className={`about-text ${showFullAbout ? 'expanded' : ''}`}
              style={{ color: template.style.textColor }}
            >
              {vcard.about}
            </p>
            {vcard.about.length > 150 && (
              <button 
                className="read-more"
                onClick={() => setShowFullAbout(!showFullAbout)}
                style={{ color: template.style.accentColor }}
              >
                {showFullAbout ? 'Show Less' : 'Read More'}
                <ChevronDown size={16} className={showFullAbout ? 'rotated' : ''} />
              </button>
            )}
          </section>
        )}

        {/* Services Section */}
        {vcard.services && vcard.services.length > 0 && (
          <section className="vcard-section">
            <h3 className="section-label" style={{ color: template.style.accentColor }}>
              Services
            </h3>
            <div className="services-grid">
              {vcard.services.map((service, index) => (
                <div 
                  key={index} 
                  className="service-chip"
                  style={{ 
                    background: `${template.style.accentColor}15`,
                    color: template.style.textColor,
                    borderColor: `${template.style.accentColor}30`
                  }}
                >
                  {service}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Gallery Section */}
        {vcard.gallery && vcard.gallery.length > 0 && (
          <section className="vcard-section">
            <h3 className="section-label" style={{ color: template.style.accentColor }}>
              Gallery
            </h3>
            <div className="gallery-scroll">
              {vcard.gallery.map((image, index) => (
                <div key={index} className="gallery-item">
                  <img src={image} alt={`Gallery ${index + 1}`} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Social Links */}
        {vcard.social && Object.values(vcard.social).some(v => v) && (
          <section className="vcard-section">
            <h3 className="section-label" style={{ color: template.style.accentColor }}>
              Connect
            </h3>
            <div className="social-grid">
              {vcard.social.instagram && (
                <a 
                  href={vcard.social.instagram.startsWith('http') ? vcard.social.instagram : `https://instagram.com/${vcard.social.instagram.replace('@', '')}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-btn instagram"
                >
                  <Instagram size={22} />
                  <span>Instagram</span>
                </a>
              )}
              {vcard.social.facebook && (
                <a 
                  href={vcard.social.facebook.startsWith('http') ? vcard.social.facebook : `https://facebook.com/${vcard.social.facebook}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-btn facebook"
                >
                  <Facebook size={22} />
                  <span>Facebook</span>
                </a>
              )}
              {vcard.social.linkedin && (
                <a 
                  href={vcard.social.linkedin.startsWith('http') ? vcard.social.linkedin : `https://linkedin.com/in/${vcard.social.linkedin}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-btn linkedin"
                >
                  <Linkedin size={22} />
                  <span>LinkedIn</span>
                </a>
              )}
              {vcard.social.youtube && (
                <a 
                  href={vcard.social.youtube.startsWith('http') ? vcard.social.youtube : `https://youtube.com/${vcard.social.youtube}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-btn youtube"
                >
                  <Youtube size={22} />
                  <span>YouTube</span>
                </a>
              )}
              {vcard.social.twitter && (
                <a 
                  href={vcard.social.twitter.startsWith('http') ? vcard.social.twitter : `https://twitter.com/${vcard.social.twitter.replace('@', '')}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-btn twitter"
                >
                  <Twitter size={22} />
                  <span>Twitter</span>
                </a>
              )}
              {vcard.social.website && (
                <a 
                  href={vcard.social.website.startsWith('http') ? vcard.social.website : `https://${vcard.social.website}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-btn website"
                >
                  <Globe size={22} />
                  <span>Website</span>
                </a>
              )}
            </div>
          </section>
        )}

        {/* Location */}
        {vcard.address && (
          <section className="vcard-section">
            <h3 className="section-label" style={{ color: template.style.accentColor }}>
              Location
            </h3>
            <a 
              href={`https://maps.google.com/?q=${encodeURIComponent(vcard.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="location-card"
              style={{ 
                background: `${template.style.accentColor}10`,
                borderColor: `${template.style.accentColor}20`
              }}
            >
              <MapPin size={20} style={{ color: template.style.accentColor }} />
              <span style={{ color: template.style.textColor }}>{vcard.address}</span>
              <ExternalLink size={16} style={{ color: template.style.accentColor }} />
            </a>
          </section>
        )}

        {/* Main Action Buttons */}
        <div className="vcard-main-actions">
          <button 
            onClick={generateVCF} 
            className="main-action-btn primary"
            style={{ background: template.style.accentColor }}
          >
            <Download size={20} />
            Save Contact
          </button>
          <button 
            onClick={shareVCard} 
            className="main-action-btn secondary"
            style={{ 
              borderColor: template.style.accentColor,
              color: template.style.accentColor
            }}
          >
            <Share2 size={20} />
            Share
          </button>
        </div>

        {/* Footer */}
        <footer className="vcard-footer">
          <p>
            Powered by <a href="/" style={{ color: template.style.accentColor }}>Digital vCard</a>
          </p>
        </footer>
      </div>
    </div>
  )
}
